<template>
    <li class="season-seal" :class="{'is-new-year': newYear, 'is-open': open, 'is-modal-open': modalOpen}"
        data-cy="header_season_seal" @mouseenter="readDoors" @focusin="readDoors">
        <button type="button" class="season-seal-button" aria-describedby="season-seal-card"
                :aria-label="`${greeting}! ${countdown ? countdown.text + '. ' : ''}${showAdvent ? 'Abrir o calendário do Advento' : 'Abrir o cartão de festas'}`"
                :aria-expanded="open ? 'true' : 'false'" @click="onClick">
            <span class="season-seal-icon" aria-hidden="true">
                <svg v-if="newYear" viewBox="-12 -12 24 24"><path :d="sparklePath"/></svg>
                <svg v-else viewBox="-12 -12 24 24"><path :d="starPath"/></svg>
            </span>
            <!-- Porta esquecida: um presentinho pula em cima do selo chamando para o calendário -->
            <span v-if="pendingDoors" class="season-seal-gift" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                    <path class="season-gift-bow" d="M12 7 C9 2 4.5 3.5 6.5 6.5 C7.6 7.8 10 7.4 12 7 Z M12 7 C15 2 19.5 3.5 17.5 6.5 C16.4 7.8 14 7.4 12 7 Z"/>
                    <rect class="season-gift-box" x="4.5" y="11" width="15" height="10" rx="1.6"/>
                    <rect class="season-gift-lid" x="3.5" y="7.4" width="17" height="4.4" rx="1.2"/>
                    <rect class="season-gift-ribbon" x="10.9" y="7.4" width="2.2" height="13.6"/>
                </svg>
            </span>
            <span class="season-seal-text">{{ greeting }}!</span>
            <span v-if="countdown && countdown.count !== null" class="season-seal-count" aria-hidden="true">
                {{ countdown.count }}<small>{{ shortUnit }}</small>
            </span>
        </button>
        <!-- Balãozinho que aparece sozinho uma vez por dia quando tem porta fechada esperando -->
        <transition name="season-nudge">
            <button v-if="nudge && pendingDoors && !modalOpen" type="button" class="season-seal-nudge"
                    data-cy="header_season_nudge" @click="openModal">
                <span class="season-seal-nudge-spark" aria-hidden="true">✦</span>{{ nudgeText }}
            </button>
        </transition>
        <!-- O cartãozinho também é clicável: dá para descer o mouse até ele e clicar em qualquer parte -->
        <span id="season-seal-card" class="season-seal-card" role="tooltip" @click="openModal">
            <strong class="season-seal-card-title">{{ greeting }}!</strong>
            <span v-if="countdown" class="season-seal-card-count" :class="{'is-day': countdown.count === null}">
                <strong v-if="countdown.count !== null">{{ countdown.count }}</strong>
                <span><small v-if="countdown.unit">{{ countdown.unit }}</small>{{ countdown.label }}</span>
            </span>
            <span class="season-seal-card-message">{{ message }}</span>
            <span class="season-seal-card-sign">Com carinho, equipe R2 Soft.</span>
            <span v-if="showAdvent" class="season-seal-advent">
                <span class="season-seal-advent-head">
                    <i class="icon-gift" aria-hidden="true"></i> Calendário do Advento
                </span>
                <span class="season-seal-advent-grid" aria-hidden="true">
                    <span v-for="day in 24" :key="day" :style="{'--i': day}"
                          :class="{'is-open': day <= adventDay && openedDoors.includes(day), 'is-locked': day > adventDay,
                                   'is-today': day === adventDay && phase !== 'christmas' && !openedDoors.includes(day)}"></span>
                </span>
                <small class="season-seal-advent-summary">{{ adventSummary }}</small>
            </span>
            <span class="season-seal-card-hint" :class="{'is-advent': showAdvent}">
                <i v-if="showAdvent" class="icon-gift" aria-hidden="true"></i>{{ hint }}
            </span>
        </span>
    </li>
</template>

<script>
// Selo de festas no cabeçalho: saudação, contagem curta (52d, 5h) e um cartãozinho ao passar o mouse. Em novembro
// e dezembro o cartãozinho mostra a prévia do calendário do Advento. Quando há porta liberada sem abrir, um
// presentinho fica pulando em cima do selo e um balãozinho avisa uma vez por dia. Clicar no selo, no cartãozinho ou
// no balão abre o modal de festas (SeasonModal.vue). Em tela de toque, o primeiro toque mostra o cartãozinho e o
// segundo abre o modal.
import {mapState} from 'vuex';
import seasonBurst from '@/Helpers/global/seasonBurst';
import {adventToday, openedAdventDoors, seasonCountdown, seasonGreeting, seasonMessage} from '@/Helpers/global/season';
import {SPARKLE_PATH, STAR_PATH} from '@/Helpers/global/seasonShapes';

const NUDGE_KEY = 'seasonNudge';
const SHORT_UNITS = {dia: 'd', dias: 'd', hora: 'h', horas: 'h', minuto: 'min', minutos: 'min'};

export default {
    name: 'SeasonSeal',
    data() {
        return {
            starPath: STAR_PATH,
            sparklePath: SPARKLE_PATH,
            open: false,
            openedDoors: [],
            nudge: false,
        };
    },
    computed: {
        ...mapState({
            now: 'seasonNow',
            modalOpen: 'seasonCardOpen',
            newYear: 'seasonNewYear',
            phase: 'seasonPhase',
        }),
        greeting() {
            return seasonGreeting(this.now);
        },
        message() {
            return seasonMessage(this.now);
        },
        countdown() {
            return seasonCountdown(this.now);
        },
        shortUnit() {
            return SHORT_UNITS[this.countdown?.unit] || '';
        },
        // Calendário do Advento (novembro mostra a prévia trancada)
        showAdvent() {
            return ['november', 'advent', 'christmasEve', 'christmas'].includes(this.phase);
        },
        adventDay() {
            return adventToday(this.now);
        },
        // Portas liberadas que a pessoa ainda não abriu: fazem o presentinho pular em cima do selo
        pendingDoors() {
            if (!this.showAdvent) return 0;
            let pending = 0;
            for (let day = 1; day <= this.adventDay; day++) {
                if (!this.openedDoors.includes(day)) pending++;
            }
            return pending;
        },
        todayPending() {
            return this.adventDay > 0 && this.phase !== 'christmas' && !this.openedDoors.includes(this.adventDay);
        },
        nudgeText() {
            const others = this.pendingDoors - (this.todayPending ? 1 : 0);
            if (this.phase === 'christmas') {
                return this.pendingDoors === 1 ? 'É Natal! Ainda falta abrir uma porta' : `É Natal! Ainda faltam ${this.pendingDoors} portas`;
            }
            if (this.todayPending && !others) return 'Psiu! A porta de hoje está esperando por você';
            if (this.todayPending) return `A porta de hoje e mais ${others} esperam por você`;
            if (others === 1 && !this.openedDoors.includes(this.adventDay - 1)) return 'Ficou uma porta para trás: a de ontem ainda está fechada';
            return others === 1 ? 'Uma porta ainda está fechada esperando por você' : `${others} portas ainda fechadas esperando por você`;
        },
        adventSummary() {
            if (!this.adventDay) return 'A primeira porta abre em 1º de dezembro';
            if (this.pendingDoors === 1) return '1 porta esperando por você';
            if (this.pendingDoors > 1) return `${this.pendingDoors} portas esperando por você`;
            return 'Todas as portas abertas';
        },
        hint() {
            const touch = this.open ? 'Toque de novo' : 'Clique';
            // Depois do Natal o cartão também guarda as cartinhas do Advento para reler (até 11/1)
            if (!this.showAdvent) return `${touch} para abrir o cartão e reler as mensagens do Advento`;
            if (!this.adventDay) return `${touch} para espiar o calendário do Advento`;
            if (this.pendingDoors) return `${touch} para abrir o calendário e descobrir a surpresa`;
            return `${touch} para rever as surpresas das portas`;
        },
    },
    watch: {
        // Fechou o modal: relê as portas abertas para o presentinho sumir, se for o caso
        modalOpen(open) {
            if (!open) this.readDoors();
        },
        // Virada com o sistema aberto (Feliz Natal, Feliz 2027): o selo solta um brilho
        phase(phase, previous) {
            if (!previous || !phase) return;
            this.$nextTick(() => {
                const button = this.$el.querySelector?.('.season-seal-button');
                if (button) seasonBurst(button, {newYear: this.newYear, origin: {x: 0.2, y: 0.5}, amount: phase === 'newYearDay' ? 40 : 18});
            });
        },
    },
    mounted() {
        this.readDoors();
        this.scheduleNudge();
    },
    beforeUnmount() {
        document.removeEventListener('pointerdown', this.close, true);
        clearTimeout(this.nudgeTimer);
    },
    methods: {
        onClick() {
            // Toque (sem hover): o primeiro toque mostra o cartão do selo
            if (!this.open && window.matchMedia?.('(hover: none)').matches) {
                this.open = true;
                document.addEventListener('pointerdown', this.close, true);
                return;
            }
            this.openModal();
        },
        openModal() {
            this.close();
            this.hideNudge();
            const button = this.$el.querySelector('.season-seal-button');
            // O foco volta para o selo quando o modal fechar
            button?.focus({preventScroll: true});
            seasonBurst(button, {newYear: this.newYear, origin: {x: 0.2, y: 0.5}, amount: 14});
            this.$store.commit('setSeasonCardOpen', true);
        },
        // As portas abertas ficam no localStorage (o modal grava); relê ao montar e ao passar o mouse
        readDoors() {
            this.openedDoors = openedAdventDoors(this.now.getFullYear());
        },
        // O balãozinho aparece uma vez por dia (por aba), um pouco depois de entrar, e some sozinho
        scheduleNudge() {
            if (!this.pendingDoors) return;
            const today = this.now.toDateString();
            try {
                if (sessionStorage.getItem(NUDGE_KEY) === today) return;
                sessionStorage.setItem(NUDGE_KEY, today);
            } catch (e) {
                // sem sessionStorage, mostra mesmo assim
            }
            this.nudgeTimer = setTimeout(() => {
                this.nudge = true;
                this.nudgeTimer = setTimeout(this.hideNudge, 8000);
            }, 1800);
        },
        hideNudge() {
            clearTimeout(this.nudgeTimer);
            this.nudge = false;
        },
        close(event) {
            if (event?.target?.closest?.('.season-seal')) return;
            this.open = false;
            document.removeEventListener('pointerdown', this.close, true);
        },
    },
};
</script>
